'use client'

import { useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'
import { navigateUrl } from '@/utils/client-request'
import { searchProductsApi } from '@/lib/commerce/services/search-api'
import { useAuth } from './useAuth'
import type { UseSearchReturn } from '@/lib/commerce/types/search'

export function useSearch(): UseSearchReturn {
  const { vendor } = useAuth()
  const pathname = usePathname()
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<any[]>([])
  const [categories, setCategories] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [history, setHistory] = useState<string[]>([])

  useEffect(() => {
    try {
      const stored = localStorage.getItem('searchHistory')
      if (stored) setHistory(JSON.parse(stored))
    } catch {}
  }, [])

  useEffect(() => {
    if (!query || query.trim().length === 0) {
      setResults([])
      setCategories([])
      return
    }
    setLoading(true)
    const t = setTimeout(async () => {
      try {
        const res = await searchProductsApi(vendor?.id, query)
        setResults(res?.error ? [] : (res?.data || []))
        setCategories(res?.error ? [] : (res?.categories || []))
      } catch {
        setResults([])
        setCategories([])
      } finally {
        setLoading(false)
      }
    }, 300)
    return () => clearTimeout(t)
  }, [query, vendor?.id])

  const saveHistory = (term: string) => {
    const trimmed = term.trim()
    if (!trimmed) return
    setHistory(prev => {
      const next = [trimmed, ...prev.filter(h => h !== trimmed)].slice(0, 6)
      try { localStorage.setItem('searchHistory', JSON.stringify(next)) } catch {}
      return next
    })
  }

  const clearHistory = () => {
    setHistory([])
    try { localStorage.removeItem('searchHistory') } catch {}
  }

  const runSearch = (term?: string) => {
    const value = (term ?? query).trim()
    if (!value) return
    saveHistory(value)
    window.location.href = navigateUrl(`/product?search=${encodeURIComponent(value)}`, pathname)
  }

  return { query, setQuery, results, categories, loading, history, saveHistory, clearHistory, runSearch }
}
